<template>
  <div class="onboarding-layout">
    <!-- Left section: white background with content -->
    <div class="onboarding-left">
      <!-- Progress bar -->
      <div class="onboarding-progress-container" :style="{ visibility: showProgress ? 'visible' : 'hidden' }">
        <div class="onboarding-progress-bar">
          <div
            class="onboarding-progress-fill"
            :style="{ width: progressBar1 + '%' }"
          ></div>
        </div>
        <div class="onboarding-progress-bar">
          <div
            class="onboarding-progress-fill"
            :style="{ width: progressBar2 + '%' }"
          ></div>
        </div>
      </div>

      <!-- Content -->
      <div class="onboarding-content">
        <!-- Title section -->
        <div class="onboarding-title-section">
          <h1 class="onboarding-title">{{ title }}</h1>
          <p v-if="subtitle" class="onboarding-subtitle">{{ subtitle }}</p>
        </div>

        <!-- Slot for step content -->
        <div class="onboarding-step-content">
          <slot></slot>
        </div>

        <!-- Navigation buttons -->
        <div class="onboarding-navigation">
          <button
            v-if="showSkipButton"
            @click="handleSkip"
            class="onboarding-btn onboarding-btn-skip"
          >
            {{ skipButtonLabel || $t('onboarding.common.skip') }}
          </button>

          <button
            @click="handleNext"
            :disabled="!canProceed"
            class="onboarding-btn onboarding-btn-next"
            :class="{ disabled: !canProceed }"
          >
            {{ computedNextButtonLabel }}
          </button>
        </div>
      </div>
    </div>

    <!-- Right section: image carousel -->
    <div class="onboarding-right">
      <div
        class="onboarding-image"
        :style="{
          backgroundImage:
            'url(' + require('@/assets/img/carousel/login_carousel.webp') + ')'
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'OnboardingLayout',

  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      default: ''
    },
    showSkipButton: {
      type: Boolean,
      default: true
    },
    skipButtonLabel: {
      type: String,
      default: null
    },
    canProceed: {
      type: Boolean,
      default: true
    },
    isLastStep: {
      type: Boolean,
      default: false
    },
    progressBar1: {
      type: Number,
      default: 100
    },
    progressBar2: {
      type: Number,
      default: 0
    },
    showProgress: {
      type: Boolean,
      default: true
    },
    nextButtonLabel: {
      type: String,
      default: null
    }
  },

  computed: {
    ...mapState('onboarding', ['currentStep', 'totalSteps']),
    ...mapGetters('onboarding', ['progress']),

    computedNextButtonLabel() {
      if (this.nextButtonLabel) return this.nextButtonLabel
      return this.isLastStep
        ? this.$t('onboarding.common.finish')
        : this.$t('onboarding.common.next')
    }
  },

  methods: {
    handleNext() {
      if (this.canProceed) {
        this.$emit('next')
      }
    },

    handleSkip() {
      this.$emit('skip')
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/vars.scss';
@import './variables.scss';

.onboarding-layout {
  display: flex;
  min-height: 100vh;
  background-color: white;
}

.onboarding-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 80px 80px 60px 80px;
  max-width: 50%;
  background-color: white;
}

.onboarding-progress-container {
  display: flex;
  gap: 8px;
  margin-bottom: 60px;
}

.onboarding-progress-bar {
  flex: 1;
  height: 4px;
  background-color: $border-ui;
  border-radius: 2px;
  overflow: hidden;
}

.onboarding-progress-fill {
  height: 100%;
  background-color: $primary-color;
  transition: width 0.3s ease;
}

.onboarding-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.onboarding-title-section {
  margin-bottom: 40px;
}

.onboarding-title {
  font-family: $thicccboi;
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 12px 0;
  color: $primary-text-color;
  line-height: 1.2;
}

.onboarding-subtitle {
  font-family: $avenirnext;
  font-size: 18px;
  font-weight: 400;
  margin: 0;
  color: $secondary-text-color;
  line-height: 1.5;
}

.onboarding-step-content {
  flex: 1;
  margin-bottom: 40px;
}

.onboarding-navigation {
  display: flex;
  gap: 16px;
}

.onboarding-btn {
  padding: 14px 32px;
  border-radius: 30px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
}

.onboarding-btn-skip {
  background-color: white;
  color: #a8a8a8;
  border: 1px solid $border-ui;

  &:hover {
    background-color: #d2d2d2;
    color: #fff;
  }
}

.onboarding-btn-next {
  background-color: $bg-ui-selected;
  color: $primary-color;

  &:hover:not(.disabled) {
    background-color: $primary-color;
    color: #fff;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;

    &:hover {
      transform: none;
    }
  }
}

.onboarding-right {
  flex: 1;
  max-width: 50%;
  position: relative;
}

.onboarding-image {
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

// Responsive
@media (max-width: 1440px) {
  .onboarding-left {
    max-width: 80%;
  }
}

@media (max-width: 1440px) {
  .onboarding-right {
    display: none;
  }
}

@media (max-width: 960px) {
  .onboarding-left {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .onboarding-layout {
    flex-direction: column;
  }

  .onboarding-left {
    max-width: 100%;
    padding: 80px 40px 40px 40px;
  }

  .onboarding-right {
    display: none;
  }

  .onboarding-title {
    font-size: 24px;
  }

  .onboarding-subtitle {
    font-size: 14px;
  }

  .onboarding-navigation {
    flex-direction: column-reverse;
  }

  .onboarding-btn {
    width: 100%;
  }
}
</style>
